import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  CreditCard, 
  Globe, 
  Copy, 
  Check, 
  ArrowRight,
  AlertTriangle,
  Info,
  ExternalLink,
  ShieldCheck,
  Users
} from "lucide-react";
import { toast } from "sonner";

// Currency symbols
const CURRENCY_SYMBOLS = {
  CAD: "$",
  USD: "$",
  EUR: "€",
  GBP: "£",
  CHF: "CHF",
  AUD: "$",
  MXN: "$",
  BRL: "R$",
};

// Get currency symbol
const getCurrencySymbol = (currency) => CURRENCY_SYMBOLS[currency] || currency;

// Format price with currency
const formatPrice = (amount, currency = "CAD") => {
  const symbol = getCurrencySymbol(currency);
  if (currency === "EUR") {
    return `${amount?.toFixed(2)} ${symbol}`;
  }
  return `${symbol}${amount?.toFixed(2)}`;
};

// Payment method configurations
const PAYMENT_METHODS = {
  interac: {
    id: "interac",
    name: "Virement Interac",
    icon: CreditCard,
    description: "Transfert électronique instantané via votre banque canadienne",
    available: ["CA"],
    color: "bg-orange-500",
    instructions: {
      title: "Instructions pour le virement Interac",
      steps: [
        "Connectez-vous à votre compte bancaire en ligne",
        "Sélectionnez 'Virement Interac' ou 'Envoi d'argent'",
        "Entrez l'adresse courriel indiquée ci-dessous",
        "Entrez le montant exact indiqué ci-dessous",
        "Dans le message/note, inscrivez votre numéro de référence",
        "Question de sécurité: 'Quel est le nom du service?' Réponse: 'billets'",
        "Confirmez et envoyez le virement"
      ],
      email_fr: "info@transfertdebillets.ca",
      email_en: "info@tickettransfer.ca",
      securityQuestion: "Quel est le nom du service?",
      securityAnswer: "billets"
    }
  },
  wise: {
    id: "wise",
    name: "Wise",
    icon: Globe,
    description: "Transfert entre particuliers dans votre devise",
    available: ["*"],
    excludeCountries: ["CA"],
    color: "bg-green-500",
    instructions: {
      title: "Instructions pour le paiement via Wise",
      steps: [
        "Connectez-vous à votre compte Wise ou créez-en un sur wise.com",
        "Cliquez sur 'Envoyer de l'argent'",
        "Entrez le montant exact dans votre devise",
        "Entrez l'adresse email indiquée ci-dessous",
        "Dans la référence, inscrivez votre numéro de référence",
        "Confirmez et envoyez le paiement",
        "Le paiement sera traité sous 1-2 jours ouvrables"
      ],
      email_fr: "info@transfertdebillets.ca",
      email_en: "info@tickettransfer.ca",
      wiseLink: "https://wise.com"
    }
  }
};

const PaymentMethodSelector = ({ 
  transaction, 
  user, 
  onPaymentMethodSelected,
  totalAmount,
  platformFee = null  // Now calculated based on unit price
}) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [copied, setCopied] = useState({});

  // Fee calculation constants - based on TOTAL transfer amount
  const FEE_TIER_LOW = 6.95;
  const FEE_TIER_MID = 9.95;
  const FEE_TIER_HIGH = 19.95;
  const FEE_THRESHOLD_LOW = 125.00;
  const FEE_THRESHOLD_HIGH = 250.00;

  // Get the transaction currency (defaults to user's currency or CAD)
  const transactionCurrency = transaction?.currency || user?.currency || "CAD";
  
  // Calculate amounts in transaction currency
  const ticketPrice = parseFloat(transaction?.price || 0);
  const ticketCount = parseInt(transaction?.ticket_count || transaction?.num_tickets || 1);
  
  // Calculate fee based on TOTAL transfer amount
  const getFee = (total) => {
    if (total <= FEE_THRESHOLD_LOW) return FEE_TIER_LOW;
    if (total <= FEE_THRESHOLD_HIGH) return FEE_TIER_MID;
    return FEE_TIER_HIGH;
  };
  const fee = platformFee !== null ? platformFee : getFee(ticketPrice);
  const total = totalAmount || (ticketPrice + fee);

  // Determine available payment methods based on user's country
  const availableMethods = Object.values(PAYMENT_METHODS).filter(method => {
    if (method.excludeCountries?.includes(user?.country)) {
      return false;
    }
    if (method.available.includes("*")) {
      return !method.excludeCountries?.includes(user?.country);
    }
    return method.available.includes(user?.country);
  });

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [field]: true });
    toast.success("Copié dans le presse-papier");
    setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
  };

  const handleSelectMethod = (method) => {
    setSelectedMethod(method);
    if (onPaymentMethodSelected) {
      onPaymentMethodSelected(method.id);
    }
  };

  const CopyButton = ({ text, field }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleCopy(text, field)}
      className="h-8 px-2"
    >
      {copied[field] ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Choisissez votre méthode de paiement</h3>
        <p className="text-sm text-gray-500 mt-1">
          Sélectionnez la méthode de paiement qui vous convient le mieux
        </p>
      </div>

      {/* Amount Display */}
      <Card className="bg-gradient-to-r from-brand-50 to-blue-50 border-brand-200">
        <CardContent className="pt-4">
          <div className="space-y-3">
            {/* Ticket Price */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Prix des billets</span>
              <span className="font-semibold">{formatPrice(ticketPrice, transactionCurrency)}</span>
            </div>
            
            {/* Platform Fee */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Frais de service Middleman</span>
              <span className="font-semibold">{formatPrice(fee, transactionCurrency)}</span>
            </div>
            
            {/* Divider */}
            <div className="border-t border-brand-200 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-brand-700">Total à payer</span>
                <span className="text-2xl font-bold text-brand-700">
                  {formatPrice(total, transactionCurrency)}
                </span>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-3">
            Référence: <span className="font-mono font-medium">{transaction?.reference_number}</span>
          </p>
        </CardContent>
      </Card>

      {/* Payment Method Buttons */}
      <div className="grid gap-3">
        {availableMethods.map((method) => {
          const Icon = method.icon;
          const isSelected = selectedMethod?.id === method.id;
          
          return (
            <Button
              key={method.id}
              variant={isSelected ? "default" : "outline"}
              className={`h-auto py-4 justify-start ${isSelected ? method.color + ' text-white' : ''}`}
              onClick={() => handleSelectMethod(method)}
              data-testid={`payment-method-${method.id}`}
            >
              <Icon className="h-6 w-6 mr-3" />
              <div className="text-left">
                <div className="font-semibold">{method.name}</div>
                <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                  {method.description}
                </div>
              </div>
              {isSelected && <Check className="h-5 w-5 ml-auto" />}
            </Button>
          );
        })}
      </div>

      {/* Payment Instructions */}
      {selectedMethod && (
        <Card className="border-2 border-brand-200 animate-in slide-in-from-top-2">
          <CardHeader className={`${selectedMethod.color} text-white rounded-t-lg`}>
            <CardTitle className="flex items-center gap-2">
              {(() => {
                const Icon = selectedMethod.icon;
                return <Icon className="h-5 w-5" />;
              })()}
              {selectedMethod.instructions.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* Steps */}
            <div className="space-y-2">
              {selectedMethod.instructions.steps.map((step, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <p className="text-sm text-gray-700">{step}</p>
                </div>
              ))}
            </div>

            {/* Important Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-gray-900">Informations importantes</h4>
              
              {/* Reference Number */}
              <div className="flex items-center justify-between bg-white rounded p-2 border">
                <div>
                  <p className="text-xs text-gray-500">Numéro de référence</p>
                  <p className="font-mono font-bold">{transaction?.reference_number}</p>
                </div>
                <CopyButton text={transaction?.reference_number} field="reference" />
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between bg-white rounded p-2 border">
                <div>
                  <p className="text-xs text-gray-500">Montant exact à envoyer</p>
                  <p className="font-bold text-lg">{formatPrice(total, transactionCurrency)}</p>
                </div>
                <CopyButton text={total?.toFixed(2)} field="amount" />
              </div>

              {/* Email (for Interac/Wise) */}
              {(selectedMethod.instructions.email_fr || selectedMethod.instructions.email_en) && (
                <div className="flex items-center justify-between bg-white rounded p-2 border">
                  <div>
                    <p className="text-xs text-gray-500">Adresse courriel</p>
                    <p className="font-mono">{selectedMethod.instructions.email_fr || selectedMethod.instructions.email_en}</p>
                    {selectedMethod.instructions.email_en && selectedMethod.instructions.email_fr !== selectedMethod.instructions.email_en && (
                      <p className="text-xs text-gray-400 mt-1">
                        English: {selectedMethod.instructions.email_en}
                      </p>
                    )}
                  </div>
                  <CopyButton text={selectedMethod.instructions.email_fr || selectedMethod.instructions.email_en} field="email" />
                </div>
              )}

              {/* Security Question (for Interac) */}
              {selectedMethod.instructions.securityQuestion && (
                <>
                  <div className="flex items-center justify-between bg-white rounded p-2 border">
                    <div>
                      <p className="text-xs text-gray-500">Question de sécurité</p>
                      <p className="font-medium">{selectedMethod.instructions.securityQuestion}</p>
                    </div>
                    <CopyButton text={selectedMethod.instructions.securityQuestion} field="question" />
                  </div>
                  <div className="flex items-center justify-between bg-amber-50 rounded p-2 border border-amber-200">
                    <div>
                      <p className="text-xs text-amber-600">Réponse</p>
                      <p className="font-bold text-amber-800">{selectedMethod.instructions.securityAnswer}</p>
                    </div>
                    <CopyButton text={selectedMethod.instructions.securityAnswer} field="answer" />
                  </div>
                </>
              )}

              {/* Wise Links - Two Buttons */}
              {selectedMethod.id === 'wise' && (
                <div className="space-y-3">
                  {/* Button for existing Wise users */}
                  <a 
                    href="https://wise.com/pay/business/middlemansecuretransactionsinc?utm_source=quick_pay"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 text-white rounded-lg p-3 hover:bg-green-600 transition-colors font-semibold"
                    data-testid="wise-pay-button"
                  >
                    <Globe className="h-5 w-5" />
                    Payer avec Wise
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  
                  {/* Button for new users - invitation link */}
                  <a 
                    href="https://wise.com/invite/ihpn/erich2734"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-white text-green-600 border-2 border-green-500 rounded-lg p-3 hover:bg-green-50 transition-colors font-medium"
                    data-testid="wise-create-account-button"
                  >
                    <Users className="h-5 w-5" />
                    Pas de compte Wise? Créez-en un ici
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>

            {/* Protection Notice - Reassurance */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-green-100 rounded-full">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800 mb-1">On vous protège!</h4>
                  <p className="text-sm text-green-700">
                    Nous conserverons votre paiement jusqu'à la réception et à la vérification des billets du vendeur. 
                    Si ce dernier ne transmet pas les billets dans les délais convenus, nous vous rembourserons rapidement.
                  </p>
                </div>
              </div>
            </div>

            {/* Warning */}
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                <strong>Important:</strong> Assurez-vous d'envoyer le montant exact et d'inclure le numéro de référence. 
                Le traitement sera retardé si les informations sont incorrectes.
              </AlertDescription>
            </Alert>

            {/* Confirmation Button */}
            <Button 
              className="w-full bg-brand-600 hover:bg-brand-700"
              onClick={() => toast.success("Instructions notées! Effectuez votre paiement puis revenez confirmer.")}
              data-testid="payment-instructions-understood"
            >
              J'ai compris, je vais effectuer le paiement
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Help Section */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-sm">
          Besoin d'aide? Contactez notre support à{" "}
          <a href="mailto:support@transfertdebillets.ca" className="text-brand-600 hover:underline">
            support@transfertdebillets.ca
          </a>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default PaymentMethodSelector;
export { PAYMENT_METHODS, CURRENCY_SYMBOLS, formatPrice };
